export function isInsecureCaptureError(err: unknown) {
  const message = String((err as Error)?.message || err || "")
  return /insecure|tainted|cross-origin|SecurityError|Load failed|Failed to fetch|NetworkError|export a still|not ready|could not move|could not load|could not capture/i.test(message)
}

export async function waitForVideoData(video: HTMLVideoElement) {
  video.muted = true
  video.playsInline = true
  video.preload = "auto"
  if (video.readyState >= 2 && video.videoWidth) return
  await new Promise<void>((resolve, reject) => {
    const timeout = window.setTimeout(() => {
      cleanup()
      if (video.readyState >= 2 && video.videoWidth) resolve()
      else reject(new Error("Video is not ready yet."))
    }, 10000)
    const onReady = () => {
      if (video.readyState >= 2 && video.videoWidth) {
        cleanup()
        resolve()
      }
    }
    const onError = () => {
      cleanup()
      reject(new Error("Could not load this video."))
    }
    const cleanup = () => {
      window.clearTimeout(timeout)
      video.removeEventListener("loadeddata", onReady)
      video.removeEventListener("loadedmetadata", onReady)
      video.removeEventListener("canplay", onReady)
      video.removeEventListener("canplaythrough", onReady)
      video.removeEventListener("error", onError)
    }
    video.addEventListener("loadeddata", onReady)
    video.addEventListener("loadedmetadata", onReady)
    video.addEventListener("canplay", onReady)
    video.addEventListener("canplaythrough", onReady)
    video.addEventListener("error", onError)
    if (video.readyState >= 2 && video.videoWidth) {
      cleanup()
      resolve()
    }
  })
}

export async function seekVideo(video: HTMLVideoElement, time: number) {
  await waitForVideoData(video)
  const duration = Number.isFinite(video.duration) ? video.duration : 0
  const nextTime = duration > 0 ? Math.min(Math.max(time, 0), Math.max(duration - 0.05, 0)) : Math.max(time, 0)
  if (Math.abs(video.currentTime - nextTime) < 0.04 && video.readyState >= 2 && video.videoWidth) return
  await new Promise<void>((resolve, reject) => {
    const timeout = window.setTimeout(() => {
      cleanup()
      if (video.readyState >= 2) resolve()
      else reject(new Error("Could not move to that frame."))
    }, 4000)
    const onSeeked = () => {
      cleanup()
      resolve()
    }
    const onError = () => {
      cleanup()
      reject(new Error("Could not move to that frame."))
    }
    const cleanup = () => {
      window.clearTimeout(timeout)
      video.removeEventListener("seeked", onSeeked)
      video.removeEventListener("error", onError)
    }
    video.addEventListener("seeked", onSeeked)
    video.addEventListener("error", onError)
    try {
      video.currentTime = nextTime
    } catch {
      cleanup()
      reject(new Error("Could not move to that frame."))
    }
  })
}

async function fileFromCanvasBlob(blob: Blob) {
  const buffer = await blob.arrayBuffer()
  if (!buffer.byteLength) throw new Error("Could not capture this frame.")
  return new File([buffer], `reelhood-cover-${Date.now()}.jpg`, { type: "image/jpeg" })
}

export function captureVideoFrame(video: HTMLVideoElement, quality = 0.82) {
  if (!video.videoWidth || !video.videoHeight) {
    throw new Error("Video is not ready yet.")
  }
  const maxSide = 1080
  const scale = Math.min(1, maxSide / Math.max(video.videoWidth, video.videoHeight))
  const canvas = document.createElement("canvas")
  canvas.width = Math.max(1, Math.round(video.videoWidth * scale))
  canvas.height = Math.max(1, Math.round(video.videoHeight * scale))
  const ctx = canvas.getContext("2d")
  if (!ctx) throw new Error("Could not capture this frame.")
  try {
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height)
  } catch (err) {
    if (isInsecureCaptureError(err)) {
      throw new Error("Could not export a still from this clip.")
    }
    throw err
  }
  return new Promise<File>((resolve, reject) => {
    try {
      canvas.toBlob(
        (blob) => {
          if (!blob) {
            reject(new Error("Could not capture this frame."))
            return
          }
          void fileFromCanvasBlob(blob).then(resolve, reject)
        },
        "image/jpeg",
        quality,
      )
    } catch (err) {
      reject(
        isInsecureCaptureError(err)
          ? new Error("Could not export a still from this clip.")
          : err,
      )
    }
  })
}

export async function coverFileFromVideo(video: HTMLVideoElement, time?: number) {
  const stamp = typeof time === "number" ? time : video.currentTime || 0
  try {
    await seekVideo(video, stamp)
  } catch (err) {
    if (!video.videoWidth) throw err
  }
  await new Promise<void>((resolve) => {
    requestAnimationFrame(() => requestAnimationFrame(() => resolve()))
  })
  return captureVideoFrame(video)
}

export async function coverFileFromSrc(src: string, time: number, preview?: HTMLVideoElement | null) {
  if (preview) {
    try {
      return await coverFileFromVideo(preview, time)
    } catch (err) {
      if (!isInsecureCaptureError(err) || (!src.startsWith("blob:") && !src.startsWith("data:"))) {
        throw err
      }
    }
  }
  if (!src) throw new Error("No clip to capture.")
  if (!src.startsWith("blob:") && !src.startsWith("data:")) {
    throw new Error("Could not export a still from this clip.")
  }
  const helper = document.createElement("video")
  helper.muted = true
  helper.playsInline = true
  helper.preload = "auto"
  helper.src = src
  helper.style.cssText = "position:fixed;left:0;top:0;width:1px;height:1px;opacity:0;pointer-events:none;"
  document.body.appendChild(helper)
  try {
    return await coverFileFromVideo(helper, time)
  } finally {
    helper.pause()
    helper.removeAttribute("src")
    helper.remove()
  }
}
